/**
 * Custom scripts.
 *
 * We prefer to keep Horizon vendor JavaScript untouched. Going forward, we should add
 * progressive enhancements here.
 */

// Horizon renders autoplay videos inline but leaves their deferred poster visible.
// Reveal them on actual playback, retaining the poster if autoplay is blocked.
/** @param {ParentNode} [root] */
function initializeGaggleAutoplayVideos(root = document) {
  root.querySelectorAll('deferred-media[autoplay] > video').forEach((video) => {
    if (!(video instanceof HTMLVideoElement) || video.dataset.autoplayInitialized === 'true') return;
    video.dataset.autoplayInitialized = 'true';

    const revealVideo = () => {
      const container = video.parentElement;
      container?.setAttribute('data-media-loaded', 'true');
      const poster = container?.querySelector('[ref="deferredMediaPlayButton"]');
      poster?.classList.add('deferred-media__playing');
      if (poster instanceof HTMLButtonElement) poster.tabIndex = -1;
    };

    video.addEventListener('playing', revealVideo, { once: true });
    if (!video.paused) revealVideo();
  });
}

initializeGaggleAutoplayVideos();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initializeGaggleAutoplayVideos(event.target);
});

const gaggleCardSwatchListsPending = new WeakSet();
const gaggleCardSwatchListObservers = new WeakMap();

initializeGaggleCardSwatches();

document.addEventListener(
  'variant:update',
  (event) => {
    const variantEvent = /** @type {Event & { detail?: { data?: { html?: Document | Element | null } } }} */ (event);

    const eventTarget = variantEvent.target instanceof Element ? variantEvent.target : null;
    const productCard = eventTarget?.closest('product-card');
    const html = variantEvent.detail?.data?.html;
    const gaggleCardRendering = html?.querySelector('[data-gaggle-product-card-rendering]') ?? (html instanceof Element ? html : null);

    requestAnimationFrame(() => {
      if (productCard) {
        updateGaggleCardPrices(productCard, gaggleCardRendering);
        updateGaggleCardDiscount(productCard, gaggleCardRendering);

        updateGaggleCardGallery(productCard);
        return;
      }

      updateGaggleVariantScopedBlocks(eventTarget?.closest('.shopify-section, dialog') ?? null, html);
    });
  },
  true
);

/**
 * Updates Gaggle's card price blocks from the lightweight Section Rendering
 * response while preserving merchant-controlled block styling in the live card.
 *
 * @param {Element} productCard
 * @param {Element | null | undefined} gaggleCardRendering
 */
function updateGaggleCardPrices(productCard, gaggleCardRendering) {
  if (!gaggleCardRendering) return;

  const newPriceBlock = gaggleCardRendering.querySelector(`[data-gaggle-price-role='current']`);
  const newPriceContainer = newPriceBlock?.querySelector(`[ref='priceContainer']`);
  const priceBlocks = productCard.querySelectorAll(`[data-gaggle-price-role='current']`);

  if (newPriceContainer && priceBlocks.length) {
    for (const priceBlock of priceBlocks) {
      const priceContainer = priceBlock.querySelector(`[ref='priceContainer']`);

      priceContainer?.replaceWith(newPriceContainer.cloneNode(true));
    }
  }

  const newMsrpBlock = gaggleCardRendering.querySelector('.custom-msrp');

  if (newMsrpBlock instanceof HTMLElement) {
    for (const msrpBlock of productCard.querySelectorAll('.custom-msrp')) {
      if (msrpBlock instanceof HTMLElement) {
        updateGaggleMsrpPrice(msrpBlock, newMsrpBlock);
      }
    }
  }

  const newReferencePriceBlock = gaggleCardRendering.querySelector(`[data-gaggle-price-role='reference']`);

  if (newReferencePriceBlock instanceof HTMLElement) {
    for (const referencePriceBlock of productCard.querySelectorAll(`[data-gaggle-price-role='reference'][data-block-id]`)) {
      if (referencePriceBlock instanceof HTMLElement) {
        updateGaggleReferencePrice(referencePriceBlock, newReferencePriceBlock);
      }
    }
  }

  const newCompareAtPriceBlock = gaggleCardRendering.querySelector(`[data-gaggle-price-role='compare-at']`);

  if (newCompareAtPriceBlock instanceof HTMLElement) {
    for (const compareAtPriceBlock of productCard.querySelectorAll(`[data-gaggle-price-role='compare-at'][data-block-id]`)) {
      if (compareAtPriceBlock instanceof HTMLElement) {
        updateGaggleCompareAtPrice(compareAtPriceBlock, newCompareAtPriceBlock);
      }
    }
  }

  const newDebugBlock = gaggleCardRendering.querySelector('.custom-price-debug');

  if (newDebugBlock instanceof HTMLElement) {
    for (const debugBlock of productCard.querySelectorAll('.custom-price-debug')) {
      if (debugBlock instanceof HTMLElement) {
        updateGagglePriceDebug(debugBlock, newDebugBlock);
      }
    }
  }
}

/**
 * @param {Element} productCard
 * @param {Element | null | undefined} gaggleCardRendering
 */
function updateGaggleCardDiscount(productCard, gaggleCardRendering) {
  if (!gaggleCardRendering) return;

  const newDiscountBlock = gaggleCardRendering.querySelector('.custom-card-discount-gaggle');
  if (!(newDiscountBlock instanceof HTMLElement)) return;

  for (const currentDiscountBlock of productCard.querySelectorAll('.custom-card-discount-gaggle')) {
    if (currentDiscountBlock instanceof HTMLElement) {
      updateGaggleCardDiscountBlock(currentDiscountBlock, newDiscountBlock);
    }
  }
}

/**
 * Updates discount state from Section Rendering while preserving the live block's
 * merchant-controlled styles, spacing, and typography classes.
 *
 * @param {HTMLElement} currentDiscountBlock
 * @param {HTMLElement} newDiscountBlock
 */
function updateGaggleCardDiscountBlock(currentDiscountBlock, newDiscountBlock) {
  const referenceSource = newDiscountBlock.dataset.referenceSource;
  const hasDiscount = newDiscountBlock.dataset.hasDiscount === 'true';

  for (const className of [
    'custom-card-discount-gaggle--sale',
    'custom-card-discount-gaggle--msrp',
    'custom-card-discount-gaggle--empty',
  ]) {
    currentDiscountBlock.classList.remove(className);
  }

  if (referenceSource) {
    currentDiscountBlock.classList.add(`custom-card-discount-gaggle--${referenceSource}`);
  }

  currentDiscountBlock.classList.toggle('custom-card-discount-gaggle--empty', !hasDiscount);

  if (newDiscountBlock.dataset.productId) {
    currentDiscountBlock.setAttribute('data-product-id', newDiscountBlock.dataset.productId);
  }

  currentDiscountBlock.dataset.referenceSource = referenceSource ?? '';
  currentDiscountBlock.dataset.hasDiscount = String(hasDiscount);

  const newBadge = newDiscountBlock.querySelector('.custom-card-discount-gaggle__badge');
  const currentBadge = currentDiscountBlock.querySelector('.custom-card-discount-gaggle__badge');

  if (!currentBadge && newBadge) {
    currentDiscountBlock.append(newBadge.cloneNode(true));
    return;
  }

  if (!currentBadge) return;

  const isEmpty = !hasDiscount || newBadge?.hasAttribute('hidden');

  currentBadge.toggleAttribute('hidden', isEmpty);
  currentBadge.textContent = isEmpty ? '' : getGaggleCardDiscountText(currentDiscountBlock, newDiscountBlock);
}

/**
 * @param {HTMLElement} currentDiscountBlock
 * @param {HTMLElement} newDiscountBlock
 */
function getGaggleCardDiscountText(currentDiscountBlock, newDiscountBlock) {
  const referenceSource = newDiscountBlock.dataset.referenceSource;

  if (referenceSource === 'sale') {
    return newDiscountBlock.dataset.saleWarning === 'true'
      ? 'Sale'
      : (currentDiscountBlock.dataset.saleLabel || 'Summer Sale!');
  }

  if (referenceSource === 'msrp') {
    const savingsDisplay = newDiscountBlock.dataset.savingsDisplay;
    const msrpSuffix = currentDiscountBlock.dataset.msrpSuffix;

    return [savingsDisplay, msrpSuffix].filter(Boolean).join(' ');
  }

  return '';
}

/**
 * Updates plain Gaggle variant-aware blocks outside product cards. Product-price
 * custom elements handle Current/Reference/Compare At themselves.
 *
 * @param {Element | null} root
 * @param {Document | Element | null | undefined} html
 */
function updateGaggleVariantScopedBlocks(root, html) {
  if (!root || !html) return;

  for (const currentDiscountBlock of root.querySelectorAll('.custom-card-discount-gaggle[data-block-id]')) {
    if (!(currentDiscountBlock instanceof HTMLElement)) continue;

    const newDiscountBlock = findGaggleRenderedBlock(
      html,
      '.custom-card-discount-gaggle[data-block-id]',
      currentDiscountBlock.dataset.blockId
    );

    if (newDiscountBlock) {
      updateGaggleCardDiscountBlock(currentDiscountBlock, newDiscountBlock);
    }
  }

  for (const debugBlock of root.querySelectorAll('.custom-price-debug[data-block-id]')) {
    if (!(debugBlock instanceof HTMLElement)) continue;

    const newDebugBlock = findGaggleRenderedBlock(
      html,
      '.custom-price-debug[data-block-id]',
      debugBlock.dataset.blockId
    );

    if (newDebugBlock) {
      updateGagglePriceDebug(debugBlock, newDebugBlock);
    }
  }
}

/**
 * @param {Document | Element} html
 * @param {string} selector
 * @param {string | undefined} blockId
 * @returns {HTMLElement | null}
 */
function findGaggleRenderedBlock(html, selector, blockId) {
  if (!blockId) return null;

  for (const block of html.querySelectorAll(selector)) {
    if (block instanceof HTMLElement && block.dataset.blockId === blockId) {
      return block;
    }
  }

  return null;
}

/**
 * @param {HTMLElement} msrpBlock
 * @param {HTMLElement} newMsrpBlock
 */
function updateGaggleMsrpPrice(msrpBlock, newMsrpBlock) {
  if (newMsrpBlock.dataset.productId) {
    msrpBlock.setAttribute('data-product-id', newMsrpBlock.dataset.productId);
  }

  const msrpPrice = msrpBlock.querySelector('.product-msrp__price');
  const newMsrpPrice = newMsrpBlock.querySelector('.product-msrp__price');

  if (msrpPrice && newMsrpPrice) {
    msrpPrice.textContent = newMsrpPrice.textContent;
  }
}

/**
 * @param {HTMLElement} debugBlock
 * @param {HTMLElement} newDebugBlock
 */
function updateGagglePriceDebug(debugBlock, newDebugBlock) {
  if (newDebugBlock.dataset.productId) {
    debugBlock.setAttribute('data-product-id', newDebugBlock.dataset.productId);
  }

  const newDebugList = newDebugBlock.querySelector('.custom-price-debug__list');
  const debugList = debugBlock.querySelector('.custom-price-debug__list');

  if (newDebugList && debugList) {
    debugList.replaceWith(newDebugList.cloneNode(true));
  }
}

/**
 * @param {HTMLElement} referencePriceBlock
 * @param {HTMLElement} newReferencePriceBlock
 */
function updateGaggleReferencePrice(referencePriceBlock, newReferencePriceBlock) {
  const isEmpty =
    newReferencePriceBlock.hasAttribute('hidden') ||
    newReferencePriceBlock.classList.contains('custom-reference-price--empty');

  referencePriceBlock.toggleAttribute('hidden', isEmpty);
  referencePriceBlock.classList.toggle('custom-reference-price--empty', isEmpty);

  if (isEmpty) {
    const priceContainer = referencePriceBlock.querySelector(`[ref='priceContainer']`);
    priceContainer?.replaceChildren();
    priceContainer?.toggleAttribute('hidden', true);
    return;
  }

  const priceContainer = referencePriceBlock.querySelector(`[ref='priceContainer']`);
  const newPriceContainer = newReferencePriceBlock.querySelector(`[ref='priceContainer']`);

  if (newPriceContainer) {
    if (priceContainer) {
      priceContainer.replaceWith(newPriceContainer.cloneNode(true));
    } else {
      referencePriceBlock.append(newPriceContainer.cloneNode(true));
    }
  }

  const referencePrice = referencePriceBlock.querySelector('.product-msrp__price');
  referencePrice?.classList.add('compare-at-price');

  const referenceSource = newReferencePriceBlock.dataset.referenceSource;
  if (referenceSource !== undefined) {
    referencePriceBlock.dataset.referenceSource = referenceSource;
  }

  const referenceLabel =
    referenceSource === 'msrp' ? referencePriceBlock.dataset.msrpLabel : referencePriceBlock.dataset.compareAtLabel;
  const showLabel = referencePriceBlock.dataset.showLabel !== 'false';
  const hiddenLabel = referencePriceBlock.querySelector('.visually-hidden');
  const visibleLabel = referencePriceBlock.querySelector('.product-msrp__label');

  if (hiddenLabel) {
    hiddenLabel.textContent = referenceLabel ? `${referenceLabel}\u00a0` : '';
  }

  if (visibleLabel) {
    if (showLabel && referenceLabel) {
      visibleLabel.textContent = referenceLabel;
    } else {
      visibleLabel.remove();
    }
  }
}

/**
 * @param {HTMLElement} compareAtPriceBlock
 * @param {HTMLElement} newCompareAtPriceBlock
 */
function updateGaggleCompareAtPrice(compareAtPriceBlock, newCompareAtPriceBlock) {
  const isEmpty =
    newCompareAtPriceBlock.hasAttribute('hidden') ||
    newCompareAtPriceBlock.classList.contains('custom-variant-compare-at--empty');

  compareAtPriceBlock.toggleAttribute('hidden', isEmpty);
  compareAtPriceBlock.classList.toggle('custom-variant-compare-at--empty', isEmpty);

  const priceContainer = compareAtPriceBlock.querySelector(`[ref='priceContainer']`);
  const newPriceContainer = newCompareAtPriceBlock.querySelector(`[ref='priceContainer']`);

  if (newPriceContainer) {
    priceContainer?.replaceWith(newPriceContainer.cloneNode(true));
  }

  if (isEmpty) return;

  const label = compareAtPriceBlock.dataset.label;
  const showLabel = compareAtPriceBlock.dataset.showLabel !== 'false';
  const hiddenLabel = compareAtPriceBlock.querySelector('.visually-hidden');
  const visibleLabel = compareAtPriceBlock.querySelector('.product-msrp__label');

  if (hiddenLabel) {
    hiddenLabel.textContent = label ? `${label}\u00a0` : '';
  }

  if (visibleLabel) {
    if (showLabel && label) {
      visibleLabel.textContent = label;
    } else {
      visibleLabel.remove();
    }
  }
}

function initializeGaggleCardSwatches() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => revealGaggleCardSwatches(document), { once: true });
  } else {
    revealGaggleCardSwatches(document);
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          revealGaggleCardSwatches(node);
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

/**
 * Keeps Gaggle's custom product-card swatches visible on narrow cards by
 * keeping swatch items in overflow-list's visible slot and hiding its +N slot.
 *
 * @param {Document | Element} root
 */
function revealGaggleCardSwatches(root) {
  const rootElement = root instanceof Element ? root : null;
  const cards = new Set();

  if (rootElement?.matches('.custom-product-card-gaggle')) {
    cards.add(rootElement);
  }

  const closestCard = rootElement?.closest('.custom-product-card-gaggle');
  if (closestCard) {
    cards.add(closestCard);
  }

  for (const card of root.querySelectorAll?.('.custom-product-card-gaggle') ?? []) {
    cards.add(card);
  }

  for (const card of cards) {
    for (const overflowList of card.querySelectorAll('product-swatches overflow-list')) {
      revealGaggleCardSwatchList(overflowList);
    }
  }
}

/**
 * @param {Element} overflowList
 * @param {number} [attempt]
 */
function revealGaggleCardSwatchList(overflowList, attempt = 0) {
  if (gaggleCardSwatchListsPending.has(overflowList)) return;

  gaggleCardSwatchListsPending.add(overflowList);

  customElements.whenDefined('overflow-list').then(() => {
    revealGaggleCardSwatchListWhenReady(overflowList, attempt);
  });
}

/**
 * @param {Element} overflowList
 * @param {number} attempt
 */
function revealGaggleCardSwatchListWhenReady(overflowList, attempt) {
  if (!overflowList.isConnected) {
    gaggleCardSwatchListsPending.delete(overflowList);
    return;
  }

  try {
    if (!overflowList.shadowRoot) {
      throw new Error('overflow-list is not upgraded yet.');
    }

    keepGaggleCardSwatchesVisible(overflowList);
    gaggleCardSwatchListsPending.delete(overflowList);
  } catch (error) {
    if (attempt >= 10) {
      gaggleCardSwatchListsPending.delete(overflowList);
      return;
    }

    setTimeout(() => revealGaggleCardSwatchListWhenReady(overflowList, attempt + 1), 50);
  }
}

/**
 * @param {Element} overflowList
 */
function keepGaggleCardSwatchesVisible(overflowList) {
  overflowList.removeAttribute('disabled');
  overflowList.setAttribute('data-custom-product-card-swatches-visible', 'true');

  const list = overflowList.shadowRoot?.querySelector('[part="list"]');
  if (list instanceof HTMLElement) {
    list.style.setProperty('flex-wrap', 'wrap');
    list.style.setProperty('gap', 'var(--gap-sm)');
    list.style.setProperty('height', 'auto');
    list.style.setProperty('overflow', 'visible');
  }

  const moreSlot = overflowList.shadowRoot?.querySelector('slot[name="more"]');
  if (moreSlot instanceof HTMLElement) {
    moreSlot.hidden = true;
    moreSlot.style.setProperty('display', 'none');
  }

  const placeholder = overflowList.shadowRoot?.querySelector('[part="placeholder"]');
  if (placeholder instanceof HTMLElement) {
    placeholder.hidden = true;
    placeholder.style.setProperty('display', 'none');
    placeholder.style.setProperty('width', '0');
  }

  for (const swatch of overflowList.querySelectorAll('.variant-option__swatch')) {
    swatch.removeAttribute('slot');
  }

  for (const moreItem of overflowList.querySelectorAll('[slot="more"]')) {
    if (moreItem instanceof HTMLElement) {
      moreItem.hidden = true;
    }
  }

  if (!gaggleCardSwatchListObservers.has(overflowList)) {
    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => keepGaggleCardSwatchesVisible(overflowList));
    });

    observer.observe(overflowList, {
      attributes: true,
      attributeFilter: ['slot'],
      childList: true,
      subtree: true,
    });
    gaggleCardSwatchListObservers.set(overflowList, observer);
  }
}

/**
 * Reveals the selected variant image set in the custom Gaggle card gallery.
 *
 * Horizon disables nested slideshows inside carousel sections. That is good for
 * scroll behavior, but it means product-card's slideshow.select call can be a
 * no-op in carousel cards. This fallback only updates the hidden state for the
 * custom Gaggle gallery; it leaves Horizon slideshow behavior untouched.
 *
 * @param {Element} productCard
 */
function updateGaggleCardGallery(productCard) {
  const selectedOption = productCard.querySelector(
    'swatches-variant-picker-component input[type="radio"]:checked[data-option-media-id]'
  );
  const selectedImageId = selectedOption instanceof HTMLInputElement ? selectedOption.dataset.optionMediaId : null;
  const gallery = productCard.querySelector('.custom-card-gallery-gaggle');

  if (!selectedImageId || !gallery) return;

  const variantSlides = Array.from(gallery.querySelectorAll('slideshow-slide[variant-image]'));
  if (!variantSlides.length) return;

  let firstVisibleSlide = null;

  for (const slide of variantSlides) {
    const isSelected = slide.getAttribute('slide-id') === selectedImageId;

    slide.toggleAttribute('hidden', !isSelected);
    slide.removeAttribute('reveal');

    if (isSelected && !firstVisibleSlide) {
      firstVisibleSlide = slide;
    }
  }

  for (const slide of variantSlides) {
    slide.setAttribute('aria-hidden', slide === firstVisibleSlide ? 'false' : 'true');
  }

  firstVisibleSlide?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
}
